/**
 * useDirtyCheck - Unsaved Changes Detection Hook
 *
 * Tracks form dirty state and provides confirmation before closing.
 * Prevents accidental data loss from unsaved changes.
 *
 * Features:
 * - Automatic dirty state tracking
 * - Browser beforeunload warning
 * - Customizable confirmation message
 * - Integration with react-hook-form
 *
 * @module use-dirty-check
 */

import * as React from 'react';
import { type UseFormReturn, type FieldValues } from 'react-hook-form';

// ============================================================================
// Types
// ============================================================================

/**
 * useDirtyCheck options
 */
export interface UseDirtyCheckOptions {
  /**
   * Whether dirty check is enabled
   * @default true
   */
  enabled?: boolean;

  /**
   * Custom warning message
   * @default 'You have unsaved changes. Are you sure you want to leave?'
   */
  warningMessage?: string;

  /**
   * Whether to show browser beforeunload warning
   * @default true
   */
  showBrowserWarning?: boolean;
}

/**
 * useDirtyCheck return value
 */
export interface UseDirtyCheckReturn {
  /**
   * Whether form has unsaved changes
   */
  isDirty: boolean;

  /**
   * Confirm close with user
   * Returns true if user confirms, false otherwise
   */
  confirmClose: () => boolean;

  /**
   * Mark form as clean (reset dirty state)
   */
  markClean: () => void;

  /**
   * Mark form as dirty
   */
  markDirty: () => void;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * useDirtyCheck - Unsaved changes detection
 *
 * Tracks form dirty state and prevents accidental data loss.
 * Works standalone or with react-hook-form.
 *
 * @example
 * Standalone usage
 * @example
 * With react-hook-form
 * @example
 * With useFormModal
 */
export function useDirtyCheck(options: UseDirtyCheckOptions = {}): UseDirtyCheckReturn {
  const {
    enabled = true,
    warningMessage = 'You have unsaved changes. Are you sure you want to leave?',
    showBrowserWarning = true,
  } = options;

  // ============================================================================
  // State
  // ============================================================================

  const [isDirty, setIsDirty] = React.useState(false);

  // ============================================================================
  // Effects
  // ============================================================================

  // Browser beforeunload warning
  React.useEffect(() => {
    if (!enabled || !showBrowserWarning || !isDirty) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Modern browsers ignore custom message and show their own
      e.returnValue = warningMessage;
      return warningMessage;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [enabled, showBrowserWarning, isDirty, warningMessage]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const confirmClose = React.useCallback((): boolean => {
    if (!enabled || !isDirty) return true;

    return window.confirm(warningMessage);
  }, [enabled, isDirty, warningMessage]);

  const markClean = React.useCallback(() => {
    setIsDirty(false);
  }, []);

  const markDirty = React.useCallback(() => {
    setIsDirty(true);
  }, []);

  // ============================================================================
  // Return
  // ============================================================================

  return {
    isDirty: enabled && isDirty,
    confirmClose,
    markClean,
    markDirty,
  };
}

/**
 * useDirtyCheckWithForm - Convenience wrapper for react-hook-form integration
 *
 * Automatically tracks form dirty state from react-hook-form.
 *
 * @example
 * Tracks dirty state from useForm and provides it to StandardFormModal
 */
export function useDirtyCheckWithForm<TFieldValues extends FieldValues = FieldValues>(
  form: UseFormReturn<TFieldValues>,
  options: Omit<UseDirtyCheckOptions, 'enabled'> = {}
): UseDirtyCheckReturn {
  const dirty = useDirtyCheck({
    ...options,
    enabled: form.formState.isDirty,
  });

  // Sync form's dirty state
  React.useEffect(() => {
    if (form.formState.isDirty) {
      dirty.markDirty();
    } else {
      dirty.markClean();
    }
  }, [form.formState.isDirty, dirty]);

  return dirty;
}
